import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../../shared/constants/routes";
import { useCourseContent } from "../hooks/useCourseContent";
import { useLessonAttempt } from "../hooks/useLessonAttempt";
import ContentMessage from "../components/lesson/ContentMessage";
import LessonView from "../components/lesson/LessonView";
import ContentProject from "../components/project/ContentProject";
import { LESSON_MESSAGES } from "../messages";
import type { LessonContent } from "../lessonTypes";
import type { ProjectContent } from "../projectTypes";

const DEFAULT_CONTENT_LANGUAGE = "FARSI";

/**
 * Entry point for one content item of a course.
 * Fetches the content, then renders it depending on its type.
 */
const ContentPage = () => {
    const { course_id = "", content_id = "" } = useParams<{ course_id: string; content_id: string }>();
    const navigate = useNavigate();
    const { lang } = useLanguage();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const contentLanguage = searchParams.get("language") || DEFAULT_CONTENT_LANGUAGE;

    const { content, contentType, alreadyCompleted, loading, error } = useCourseContent(
        course_id,
        content_id,
        contentLanguage
    );

    console.log(content);

    // Lessons and projects are tracked by an attempt. Replays are free and untracked: no attempt is needed.
    const isPlayable = contentType === "lesson" || contentType === "project";
    const needsAttempt = isPlayable && !alreadyCompleted;

    const attempt = useLessonAttempt({
        enabled: !loading && !error && needsAttempt && Boolean(user?.id),
        userId: user?.id,
        sessionId: user?.session_id,
        contentId: content_id
    });

    // The course map: where the learner returns after the finish screens
    const handleExit = () => navigate(ROUTES.myCourseContent(lang, course_id));

    if (loading) return <ContentMessage message={LESSON_MESSAGES.loading} />;
    if (error) return <ContentMessage message={error} />;

    if (isPlayable) {
        // The attempt request failed: don't let the learner play something that can't be saved
        if (attempt.startError) return <ContentMessage message={LESSON_MESSAGES.startFailed} />;

        // First-time content needs the attempt id before the first render
        if (needsAttempt && (attempt.starting || !attempt.lessonAttemptId)) {
            return <ContentMessage message={LESSON_MESSAGES.loading} />;
        }
    }

    switch (contentType) {
        case "lesson":
            return (
                <LessonView
                    key={content_id}
                    content={content as LessonContent}
                    userId={user?.id}
                    courseId={course_id}
                    contentId={content_id}
                    lessonAttemptId={attempt.lessonAttemptId}
                    alreadyCompleted={alreadyCompleted}
                    onExit={handleExit}
                />
            );

        case "project":
            return (
                // key: the project reads its saved progress once, when it mounts
                <ContentProject
                    key={content_id}
                    content={content as ProjectContent}
                    userId={user?.id}
                    courseId={course_id}
                    contentId={content_id}
                    lessonAttemptId={attempt.lessonAttemptId}
                    alreadyCompleted={alreadyCompleted}
                    onExit={handleExit}
                />
            );

        case "video":
            return <ContentMessage message={LESSON_MESSAGES.videoSoon} />;

        default:
            return <ContentMessage message={LESSON_MESSAGES.unsupported} />;
    }
};

export default ContentPage;